/**
 * דשבורד החצר — סגנון Travian
 * ================================================================
 * שני חצאים:
 *  שמאל: מוסדות (עם כפתור שדרוג)
 *  ימין: פעולות מהירות + דמויות
 */

import { useState } from 'react';
import type { RoundPlayer } from '../../api/client.js';
import type { WorldState, Dyn } from '../RoundScreen.js';

const BUILD_OPTIONS = [
  { type: 'yeshiva_ketana', label: 'ישיבה קטנה', icon: '📖', cost: 8000, produces: 'תורה', desc: '25 תורה/שנה' },
  { type: 'kollel', label: 'כולל', icon: '🕯', cost: 12000, produces: 'תורה + קדושה', desc: '15 תורה, 20 קדושה/שנה' },
  { type: 'beit_midrash', label: 'בית מדרש', icon: '🏛', cost: 5000, produces: 'קדושה', desc: '30 קדושה/שנה' },
  { type: 'talmud_torah', label: 'תלמוד תורה', icon: '📚', cost: 6000, produces: 'תורה', desc: '15 תורה/שנה' },
];

const INST_LABELS: Record<string, string> = {
  yeshiva_ketana: 'ישיבה קטנה',
  yeshiva_gedola: 'ישיבה גדולה',
  kollel: 'כולל',
  beit_midrash: 'בית מדרש',
  talmud_torah: 'תלמוד תורה',
  beit_din: 'בית דין',
  mikvah: 'מקווה',
};

export function Dashboard({
  state,
  dynasty,
  onIntent,
}: {
  state: WorldState;
  player: RoundPlayer;
  dynasty: Dyn;
  onIntent: (type: string, payload: Record<string, unknown>) => Promise<{ success: boolean; message?: string }>;
}) {
  const [feedback, setFeedback] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null);

  // המוסדות שלי
  const myInsts = Object.values(state.entities.institutions).filter(
    (i) => i.dynastyId === dynasty.id && i.status === 'active',
  );

  // דמויות של החצר
  const myChars = Object.values(state.entities.characters).filter(
    (c) => c.dynastyId === dynasty.id && c.status === 'alive',
  );
  const adamor = dynasty.currentAdamorId ? state.entities.characters[dynasty.currentAdamorId] : null;
  const withCareer = myChars.filter((c) => c.careerPath && c.careerPath !== 'unassigned');

  // חסידים
  const myFollowers = state.followers
    .filter((f) => f.dynastyId === dynasty.id)
    .reduce((sum, f) => sum + f.count, 0);

  const homeCity = state.entities.cities[dynasty.homeCityId];

  const doBuild = async (type: string, cost: number): Promise<void> => {
    if (dynasty.resources.gold < cost) {
      setFeedback({ msg: `אין מספיק זהב (נדרש ${cost.toLocaleString('he-IL')})`, kind: 'err' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }
    const result = await onIntent('BUILD_INSTITUTION', {
      cityId: dynasty.homeCityId,
      institutionType: type,
    });
    if (result.success) {
      setFeedback({ msg: `בנייה נשלחה — ${INST_LABELS[type]}`, kind: 'ok' });
    } else {
      setFeedback({ msg: result.message ?? 'שגיאה', kind: 'err' });
    }
    setTimeout(() => setFeedback(null), 4000);
  };

  const doUpgrade = async (instId: string): Promise<void> => {
    const result = await onIntent('UPGRADE_INSTITUTION', { institutionId: instId });
    setFeedback(
      result.success
        ? { msg: 'שדרוג נשלח', kind: 'ok' }
        : { msg: result.message ?? 'שגיאה', kind: 'err' },
    );
    setTimeout(() => setFeedback(null), 3000);
  };

  return (
    <div className="dashboard">
      <div className="dash-hero">
        <div className="dash-hero-left">
          <h1>{dynasty.name}</h1>
          <div className="dash-subtitle">
            {ETHNIC_LABELS[dynasty.ethnicity] ?? dynasty.ethnicity} · {STREAM_LABELS[dynasty.stream] ?? dynasty.stream}
            {homeCity && ` · ${homeCity.name}`}
          </div>
        </div>
        <div className="dash-hero-right">
          <div className="hero-stat">
            <span className="lbl">חסידים</span>
            <span className="val">{myFollowers.toLocaleString('he-IL')}</span>
          </div>
          <div className="hero-stat">
            <span className="lbl">מוניטין</span>
            <span className="val">{dynasty.reputation}</span>
          </div>
          <div className="hero-stat">
            <span className="lbl">מוסדות</span>
            <span className="val">{myInsts.length}</span>
          </div>
        </div>
      </div>

      {feedback && <div className={`feedback ${feedback.kind}`}>{feedback.msg}</div>}

      <div className="dash-grid">
        {/* מוסדות */}
        <section className="dash-panel">
          <h2>🏛 מוסדות</h2>
          {myInsts.length === 0 ? (
            <div className="empty">עוד לא בנית מוסד. תבחר משהו מהרשימה בצד.</div>
          ) : (
            <ul className="inst-list">
              {myInsts.map((i) => (
                <li key={i.id}>
                  <div>
                    <strong>{INST_LABELS[i.type] ?? i.type}</strong>
                    <div className="meta">רמה {i.level} · {state.entities.cities[i.cityId]?.name ?? '?'}</div>
                  </div>
                  {i.level < 5 && (
                    <button className="btn-small" onClick={() => void doUpgrade(i.id)}>
                      שדרג ל-{i.level + 1}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* בניית מוסד חדש */}
        <section className="dash-panel">
          <h2>🔨 בנה מוסד חדש</h2>
          <div className="build-grid">
            {BUILD_OPTIONS.map((opt) => {
              const canAfford = dynasty.resources.gold >= opt.cost;
              return (
                <button
                  key={opt.type}
                  className={`build-card ${!canAfford ? 'disabled' : ''}`}
                  disabled={!canAfford}
                  onClick={() => void doBuild(opt.type, opt.cost)}
                >
                  <div className="icon">{opt.icon}</div>
                  <div className="label">{opt.label}</div>
                  <div className="desc">{opt.desc}</div>
                  <div className="cost">💰 {opt.cost.toLocaleString('he-IL')}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* דמויות */}
        <section className="dash-panel">
          <h2>👤 משפחה</h2>
          {adamor && (
            <div className="char-highlight">
              <span className="role">👑 אדמו״ר</span>
              <strong>{adamor.displayName}</strong>
              <span className="age">גיל {calcAge(adamor.birthTick, state.meta.tickNumber)}</span>
            </div>
          )}
          <div className="char-list">
            {myChars
              .filter((c) => c.role !== 'adamor')
              .slice(0, 8)
              .map((c) => (
                <div key={c.id} className="char-row">
                  <span className="char-name">{c.displayName}</span>
                  <span className="char-meta">
                    גיל {calcAge(c.birthTick, state.meta.tickNumber)} · {ROLE_LABELS[c.role] ?? c.role}
                  </span>
                  {c.careerPath && c.careerPath !== 'unassigned' && (
                    <span className="career-badge">{CAREER_LABELS[c.careerPath] ?? c.careerPath}</span>
                  )}
                </div>
              ))}
          </div>
        </section>

        {/* קריירות */}
        <section className="dash-panel">
          <h2>💼 קריירות פעילות</h2>
          {withCareer.length === 0 ? (
            <div className="empty">אף דמות במסלול קריירה. הילדים יגדלו.</div>
          ) : (
            <div className="career-list">
              {withCareer.map((c) => (
                <div key={c.id} className="career-row">
                  <div>
                    <strong>{c.displayName}</strong>
                    <div className="meta">{CAREER_LABELS[c.careerPath!] ?? c.careerPath}</div>
                  </div>
                  <div className="career-stats">
                    <span>רמה {c.careerProgress?.level ?? 1}</span>
                    <span>fame {c.careerProgress?.fame ?? 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function calcAge(birthTick: number, currentTick: number): number {
  return Math.max(0, Math.floor((currentTick - birthTick) / 360));
}

const ETHNIC_LABELS: Record<string, string> = {
  ashkenazi: 'אשכנזי',
  sephardi: 'ספרדי',
  mizrahi: 'מזרחי',
  yemenite: 'תימני',
};

const STREAM_LABELS: Record<string, string> = {
  chassidic: 'חסידי',
  litvish: 'ליטאי',
  mizrahi: 'מזרחי',
  religious_zionist: 'דת״ל',
  sephardi_charedi: 'ספרדי חרדי',
};

const ROLE_LABELS: Record<string, string> = {
  adamor: 'אדמו״ר',
  emissary: 'שלוח',
  rabbi: 'רב',
  magnate: 'נגיד',
  family_member: 'בן משפחה',
};

const CAREER_LABELS: Record<string, string> = {
  adamor_heir: 'יורש',
  singer: '🎤 זמר',
  influencer: '📱 משפיען',
  politician: '🏛 פוליטיקאי',
  rabbi: '📜 רב',
};
